import { useState } from "react";
import { DateRangePicker } from "../../shared/dateRangePicker";
import { SearchSelect } from "../../shared/searchSelect";
import { SearchFiltersModal } from "../../shared/searchFiltersModal";
import stringHelpers from "../../../utilities/stringHelpers";
import { toDateString } from "../../../utilities/dateUtilities";
import { TagSelector } from "../../shared/tagSelector";
import appliedFiltersUtilities from "../../../utilities/appliedFiltersUtilities";
import arrayUtilities from "../../../utilities/arrayUtilities";
import { reportDatePresetByLabel } from "../../reports/reportsEnums";

export const LettersFiltersModal = ({
	showModal,
	handleClose,
	handleConfirm,
	initialFromDate,
	initialToDate,
	initialDatePreset,
	initialLetterTemplateId,
	initialStatus,
	initialSelectedTags = [],
	initialAppliedFilters,
	letterTemplates,
	tags = []
}) => {
	const [fromDate, setFromDate] = useState(initialFromDate)
	const [toDate, setToDate] = useState(initialToDate)
	const [datePreset, setDatePreset] = useState(initialDatePreset)
	const [letterTemplateId, setLetterTemplateId] = useState(initialLetterTemplateId)
	const [status, setStatus] = useState(initialStatus)
	const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
	const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

	const clearLocalFilters = () => {
		setFromDate(null)
		setToDate(null)
		setDatePreset(null)
		setLetterTemplateId(null)
		setStatus("")
		setSelectedTags([])
		setAppliedFilters(null)
	}

	const handleTagsChange = tagSelections => {
		setSelectedTags(tagSelections)
		setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
	}

	return (
		<SearchFiltersModal
			title="Filter Results"
			handleClose={ handleClose }
			handleConfirm={ () => handleConfirm({ fromDate, toDate, datePreset, letterTemplateId, status, selectedTags, appliedFilters }) }
			showModal={ showModal }
			key="letters-raw-data-filters-modal"
			handleReset={ clearLocalFilters }
		>
			<div className="w-100">
				<div className="mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
					<DateRangePicker
						fromDate={ fromDate }
						toDate={ toDate }
						setDateRange={ (firstDate, lastDate, presetLabel) => {
							const isFirstDateEmpty = firstDate === null || firstDate === ""
							const isLastDateEmpty = lastDate === null || lastDate === ""
							setFromDate(isFirstDateEmpty ? null : firstDate);
							setToDate(isLastDateEmpty ? null : lastDate);
							setDatePreset(stringHelpers.isNullOrEmpty(presetLabel) ? null : (reportDatePresetByLabel[presetLabel] ?? null))
							if (!stringHelpers.isNullOrEmpty(presetLabel)) {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									delete updatedFilters.From
									delete updatedFilters.To
									updatedFilters["Date Range"] = presetLabel
									return updatedFilters
								})
							}
							else {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									if (!isFirstDateEmpty && !isLastDateEmpty) {
										delete updatedFilters.From
										delete updatedFilters.To
										updatedFilters["Date Range"] = `${toDateString(new Date(firstDate))} - ${toDateString(new Date(lastDate))}`
									} else {
										if (!isFirstDateEmpty) {
											updatedFilters.From = toDateString(new Date(firstDate))
										} else {
											delete updatedFilters.From
										}
										if (!isLastDateEmpty) {
											updatedFilters.To = toDateString(new Date(lastDate))
										} else {
											delete updatedFilters.To
										}
										delete updatedFilters["Date Range"]
									}
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							}
						}}
						showClearOption
						hideLabel
						fullWidth
						dropdownPositionRight
					/>
				</div>

				<div className="d-flex flex-column mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Letter Template</label>
					<SearchSelect
						defaultLabel="All"
						width="100%"
						options={ [{ value: "", label: "All" }, ...letterTemplates.map(item => ({ value: item.id, label: item.name }))] }
						onSelect={ value => {
							if (!stringHelpers.isNullOrEmpty(value)) {
								setLetterTemplateId(value)
								const selectedTemplate = letterTemplates.find(item => item.id === value)
								setAppliedFilters(prevState => ({ ...prevState, "Letter Template": selectedTemplate.name }))
							} else {
								setLetterTemplateId(null)
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									delete updatedFilters["Letter Template"]
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							}
						} }
						selected={ !stringHelpers.isNullOrEmpty(letterTemplateId) ? letterTemplateId : null }
					/>
				</div>

				<div className="d-flex flex-column mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Status</label>
					<select
						value={ status }
						onChange={ e => {
							const value = e.target.value
							const label = e.target.options[e.target.selectedIndex].text
							setStatus(value)
							setAppliedFilters(prevState => {
								const updatedFilters = { ...prevState }
								if (stringHelpers.isNullOrEmpty(value)) {
									delete updatedFilters["Status"]
								} else {
									updatedFilters["Status"] = label
								}
								return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
							})
						} }
						className="border rounded px-2 py-1 w-100"
					>
						<option value="">All</option>
						<option value="Printed">Printed</option>
						<option value="Error">Error</option>
						<option value="Returned">Returned</option>
						<option value="Returned">Cancelled</option>
					</select>
				</div>

				{ !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
					<div className="d-flex align-items-center justify-content-between">
						<label className="m-0 mr-2 mb-1 nowrap">Tags</label>
						<button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
					</div>
					<TagSelector
						onChange={ handleTagsChange }
						selectedTags={ selectedTags }
						tags={ tags }
					/>
				</div> }
			</div>
		</SearchFiltersModal>
	)
}
